import { useEffect, useRef } from 'react';
import type { ScrollElementProps } from './scroll-element.props';

const ScrollElement = ({
  focus,
  blur,
  elementCreated,
  readonly = false,
  className,
  role,
  activeDescendantId,
  multiselectable,
  children,
}: ScrollElementProps) => {
  const createdElement = useRef<HTMLElement | undefined>(undefined);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Cleanup only on unmount; onElementCreated handles listener setup via ref callback
  useEffect(() => {
    // Capture current values for cleanup
    const currentElement = createdElement.current;
    const currentFocus = focus;
    const currentBlur = blur;

    return () => {
      // cleanup the focus and blur event listeners
      if (currentElement) {
        if (currentFocus) {
          currentElement.removeEventListener('focus', currentFocus);
        }
        if (currentBlur) {
          currentElement.removeEventListener('blur', currentBlur);
        }
      }
    };
  }, []);

  const onElementCreated = (element: HTMLElement) => {
    if (createdElement.current) {
      if (focus) {
        createdElement.current.removeEventListener('focus', focus);
      }
      if (blur) {
        createdElement.current.removeEventListener('blur', blur);
      }
    }

    createdElement.current = element;
    if (elementCreated) {
      elementCreated(element);
    }

    if (focus) {
      createdElement.current.addEventListener('focus', focus);
    }
    if (blur) {
      createdElement.current.addEventListener('blur', blur);
    }
  };

  return (
    <div
      ref={(element) => {
        if (element) onElementCreated(element);
      }}
      className={className}
      tabIndex={readonly ? -1 : 0}
      {...(role && {
        role,
        'aria-activedescendant': activeDescendantId,
        'aria-multiselectable': multiselectable,
      })}
    >
      {children}
    </div>
  );
};

export { ScrollElement };
